import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface SaasConfig {
  evolution_server_url: string;
  evolution_api_key: string;
}

export default function SettingsEvolutionApiPage() {
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';

  // ─── Admin: Global SaaS Config ───
  const [saasConfig, setSaasConfig] = useState<SaasConfig>({ evolution_server_url: '', evolution_api_key: '' });
  const [saasSaving, setSaasSaving] = useState(false);
  const [saasMessage, setSaasMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [saasTesting, setSaasTesting] = useState(false);

  // ─── End User: Instance Connection ───
  const [serverUrl, setServerUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [instanceName, setInstanceName] = useState('');
  const [status, setStatus] = useState<'connected' | 'disconnected' | 'testing'>('disconnected');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Provisioning state
  const [provisioning, setProvisioning] = useState(false);
  const [provisionMessage, setProvisionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Test message state
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Olá! Esta é uma mensagem de teste do WzapPro 🚀');
  const [sendingTest, setSendingTest] = useState(false);

  // Reconnect state
  const [reconnecting, setReconnecting] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [keepAliveActive, setKeepAliveActive] = useState(false);
  const [lastCheck, setLastCheck] = useState<string | null>(null);

  const keepAliveRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Load admin SaaS config ───
  useEffect(() => {
    if (!isAdmin) return;
    supabase
      .from('saas_config')
      .select('key, value')
      .in('key', ['evolution_server_url', 'evolution_api_key'])
      .then(({ data }) => {
        const map: Record<string, string> = {};
        for (const row of (data || [])) {
          map[row.key] = row.value;
        }
        setSaasConfig({
          evolution_server_url: map['evolution_server_url'] || '',
          evolution_api_key: map['evolution_api_key'] || '',
        });
      });
  }, [isAdmin]);

  // ─── Load company config ───
  useEffect(() => {
    if (!profile?.company_id) return;
    supabase
      .from('whatsapp_configs')
      .select('evolution_server_url, evolution_api_key, evolution_instance_name, provider')
      .eq('company_id', profile.company_id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setServerUrl(data.evolution_server_url || '');
          setApiKey(data.evolution_api_key || '');
          setInstanceName(data.evolution_instance_name || '');

          if (data.evolution_server_url && data.evolution_api_key && data.evolution_instance_name) {
            checkConnectionState(data.evolution_server_url, data.evolution_api_key, data.evolution_instance_name);
          }
        }
      });
  }, [profile?.company_id]);

  // ─── Admin handlers ───
  const handleSaveSaas = async () => {
    setSaasSaving(true);
    setSaasMessage(null);

    // Try UPDATE first (more reliable with RLS), fallback to INSERT
    const { error: err1 } = await supabase
      .from('saas_config')
      .update({ value: saasConfig.evolution_server_url, updated_at: new Date().toISOString() })
      .eq('key', 'evolution_server_url');

    if (err1 && err1.message?.includes('0 rows')) {
      await supabase
        .from('saas_config')
        .insert({ key: 'evolution_server_url', value: saasConfig.evolution_server_url, updated_at: new Date().toISOString() });
    }

    const { error: err2 } = await supabase
      .from('saas_config')
      .update({ value: saasConfig.evolution_api_key, updated_at: new Date().toISOString() })
      .eq('key', 'evolution_api_key');

    if (err2 && err2.message?.includes('0 rows')) {
      await supabase
        .from('saas_config')
        .insert({ key: 'evolution_api_key', value: saasConfig.evolution_api_key, updated_at: new Date().toISOString() });
    }

    if (err1 || err2) {
      setSaasMessage({ type: 'error', text: `Erro ao salvar: ${err1?.message || err2?.message}` });
    } else {
      setSaasMessage({ type: 'success', text: 'Configuração global salva com sucesso!' });
    }
    setSaasSaving(false);
    setTimeout(() => setSaasMessage(null), 4000);
  };

  const handleTestSaas = async () => {
    if (!saasConfig.evolution_server_url || !saasConfig.evolution_api_key) return;
    setSaasTesting(true);
    setSaasMessage(null);

    try {
      const baseUrl = saasConfig.evolution_server_url.replace(/\/+$/, '');
      const res = await fetch(`${baseUrl}/instance/fetchInstances`, {
        headers: {
          'Content-Type': 'application/json',
          apikey: saasConfig.evolution_api_key,
        },
      });

      if (res.ok) {
        setSaasMessage({ type: 'success', text: 'Servidor Evolution API está online e respondendo!' });
      } else {
        setSaasMessage({ type: 'error', text: `Servidor retornou erro ${res.status}. Verifique a URL e a API Key.` });
      }
    } catch {
      setSaasMessage({ type: 'error', text: 'Não foi possível conectar ao servidor. Verifique a URL.' });
    }

    setSaasTesting(false);
    setTimeout(() => setSaasMessage(null), 6000);
  };

  // ─── End user: Auto-provision instance ───
  const handleProvision = async () => {
    setProvisioning(true);
    setProvisionMessage(null);

    const { data, error } = await supabase.functions.invoke('provision-evolution-instance', {
      body: {},
    });

    setProvisioning(false);

    if (error || !data?.success) {
      setProvisionMessage({
        type: 'error',
        text: data?.message || error?.message || 'Falha ao criar instância.',
      });
    } else {
      setProvisionMessage({
        type: 'success',
        text: data?.alreadyExists
          ? 'Instância já existe! Atualizando...'
          : `Instância "${data.instanceName}" criada com sucesso!`,
      });
      // Refresh page to load new instance
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
    setTimeout(() => setProvisionMessage(null), 8000);
  };

  // ─── Connection state check ───
  const checkConnectionState = async (url: string, key: string, instance: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('test-whatsapp-connection', {
        body: {},
      });

      if (!error && data?.success) {
        const state = data?.state;
        if (state === 'open') {
          setStatus('connected');
          return;
        }
        setStatus('disconnected');
        return;
      }
    } catch {
      // fall through to direct check
    }

    if (!url || !key || !instance) {
      setStatus('disconnected');
      return;
    }

    try {
      const baseUrl = url.replace(/\/+$/, '');
      const stateUrl = `${baseUrl}/instance/connectionState/${instance}`;
      const stateRes = await fetch(stateUrl, {
        headers: {
          'Content-Type': 'application/json',
          apikey: key,
        },
      });

      if (stateRes.ok) {
        const stateData = await stateRes.json();
        const instanceState = stateData?.instance?.state;
        if (instanceState === 'open') {
          setStatus('connected');
        } else {
          setStatus('disconnected');
        }
      } else {
        setStatus('disconnected');
      }
    } catch {
      setStatus('disconnected');
    }
  };

  // Keep-alive
  const doKeepAliveCheck = useCallback(async () => {
    if (!serverUrl || !apiKey || !instanceName) return;
    try {
      const baseUrl = serverUrl.replace(/\/+$/, '');
      const stateUrl = `${baseUrl}/instance/connectionState/${instanceName}`;
      const stateRes = await fetch(stateUrl, {
        headers: {
          'Content-Type': 'application/json',
          apikey: apiKey,
        },
      });

      if (stateRes.ok) {
        const stateData = await stateRes.json();
        const instanceState = stateData?.instance?.state;
        setLastCheck(new Date().toLocaleTimeString('pt-BR'));

        if (instanceState === 'open') {
          setStatus('connected');
        } else {
          setStatus('disconnected');
          if (instanceState !== 'open' && !reconnecting && !qrCode) {
            handleReconnectSilent();
          }
        }
      } else {
        setStatus('disconnected');
        setLastCheck(new Date().toLocaleTimeString('pt-BR'));
      }
    } catch {
      setStatus('disconnected');
    }
  }, [serverUrl, apiKey, instanceName, reconnecting, qrCode]);

  const handleReconnectSilent = async () => {
    if (!serverUrl || !apiKey || !instanceName || reconnecting) return;
    setReconnecting(true);

    try {
      const baseUrl = serverUrl.replace(/\/+$/, '');
      const connectUrl = `${baseUrl}/instance/connect/${instanceName}`;
      const res = await fetch(connectUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          apikey: apiKey,
        },
      });

      const data = await res.json();
      if (res.ok && (data?.qrcode?.base64 || data?.base64)) {
        setQrCode(data?.qrcode?.base64 || data?.base64);
        setMessage({
          type: 'success',
          text: 'Instância desconectada! QR Code gerado automaticamente. Escaneie para reconectar.',
        });
        setTimeout(() => setMessage(null), 15000);
      }
    } catch {
      // Silent fail
    }
    setReconnecting(false);
  };

  const startKeepAlive = () => {
    if (keepAliveRef.current) return;
    setKeepAliveActive(true);
    keepAliveRef.current = setInterval(() => {
      doKeepAliveCheck();
    }, 30000);
    doKeepAliveCheck();
  };

  const stopKeepAlive = () => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
    setKeepAliveActive(false);
  };

  useEffect(() => {
    if (serverUrl && apiKey && instanceName) {
      startKeepAlive();
    }
    return () => stopKeepAlive();
  }, [serverUrl, apiKey, instanceName]);

  // ─── Test & Reconnect handlers ───
  const handleTest = async () => {
    if (!serverUrl || !apiKey) return;

    if (profile?.company_id) {
      await supabase.from('whatsapp_configs').upsert(
        {
          company_id: profile.company_id,
          provider: 'evolution',
          evolution_server_url: serverUrl,
          evolution_api_key: apiKey,
          evolution_instance_name: instanceName,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'company_id' },
      );
    }

    setTesting(true);
    setMessage(null);

    const { data, error } = await supabase.functions.invoke('test-whatsapp-connection', {
      body: {},
    });

    setTesting(false);

    if (error || !data?.success) {
      setStatus('disconnected');
      setMessage({ type: 'error', text: data?.message || error?.message || 'Falha na conexão' });
    } else {
      setStatus('connected');
      setMessage({
        type: 'success',
        text: `Conectado! Instância: ${data.instanceName || 'N/A'} — Status: ${data.state || 'N/A'}`,
      });
    }
    setTimeout(() => setMessage(null), 6000);
  };

  const handleSendTest = async () => {
    if (!serverUrl || !apiKey || !instanceName || !testPhone.trim() || !testMessage.trim()) return;

    setSendingTest(true);
    setMessage(null);

    try {
      const baseUrl = serverUrl.replace(/\/+$/, '');
      const sendUrl = `${baseUrl}/message/sendText/${instanceName}`;

      const res = await fetch(sendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: apiKey,
        },
        body: JSON.stringify({
          number: testPhone.trim(),
          text: testMessage.trim(),
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setStatus('connected');
        setMessage({
          type: 'success',
          text: `Mensagem enviada com sucesso para ${testPhone.trim()}!`,
        });
        setTestMessage('Olá! Esta é uma mensagem de teste do WzapPro 🚀');
      } else {
        const apiErrorMsg = data?.message || data?.error || 'Falha desconhecida';
        let instanceStateMsg = '';
        try {
          const stateUrl = `${baseUrl}/instance/connectionState/${instanceName}`;
          const stateRes = await fetch(stateUrl, {
            headers: { 'Content-Type': 'application/json', apikey: apiKey },
          });
          if (stateRes.ok) {
            const stateData = await stateRes.json();
            const state = stateData?.instance?.state;
            if (state === 'open') {
              setStatus('connected');
              instanceStateMsg = ` (Instância está "${state}" — erro pode ser no destinatário)`;
            } else {
              setStatus('disconnected');
              instanceStateMsg = ` (Instância está "${state || 'desconhecida'}" — reconecte o WhatsApp!)`;
            }
          } else {
            setStatus('disconnected');
            instanceStateMsg = ' (Não foi possível verificar estado da instância)';
          }
        } catch {
          instanceStateMsg = ' (Não foi possível verificar estado da instância)';
        }

        setMessage({
          type: 'error',
          text: `Erro ao enviar: ${apiErrorMsg}${instanceStateMsg}`,
        });
      }
    } catch (err: unknown) {
      setMessage({
        type: 'error',
        text: `Erro: ${err instanceof Error ? err.message : 'Erro inesperado'}`,
      });
      setStatus('disconnected');
    }

    setSendingTest(false);
    setTimeout(() => setMessage(null), 8000);
  };

  const handleReconnect = async () => {
    if (!serverUrl || !apiKey || !instanceName) return;

    setReconnecting(true);
    setMessage(null);
    setQrCode(null);

    try {
      const baseUrl = serverUrl.replace(/\/+$/, '');
      const connectUrl = `${baseUrl}/instance/connect/${instanceName}`;

      const res = await fetch(connectUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          apikey: apiKey,
        },
      });

      const data = await res.json();

      if (res.ok) {
        const qr = data?.qrcode?.base64 || data?.base64 || null;
        if (qr) {
          setQrCode(qr);
          setMessage({
            type: 'success',
            text: 'QR Code gerado! Escaneie com o WhatsApp para reconectar a instância.',
          });
        } else {
          setStatus('disconnected');
          setMessage({
            type: 'error',
            text: 'Instância não retornou QR Code. Verifique o status no servidor Evolution API.',
          });
        }
      } else {
        setMessage({
          type: 'error',
          text: `Erro ao reconectar: ${data?.message || data?.error || 'Falha desconhecida'}`,
        });
      }
    } catch (err: unknown) {
      setMessage({
        type: 'error',
        text: `Erro: ${err instanceof Error ? err.message : 'Erro inesperado'}`,
      });
    }

    setReconnecting(false);
    setTimeout(() => setMessage(null), 10000);
  };

  const isConfigured = !!(serverUrl && apiKey && instanceName);

  // ═══════════════════════════════════════
  // ADMIN VIEW
  // ═══════════════════════════════════════
  if (isAdmin) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground-950">Evolution API — Configuração SaaS</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Configure o servidor Evolution API global. Todas as empresas dos seus clientes usarão este servidor automaticamente.
          </p>
        </div>

        {saasMessage && (
          <div
            className={`rounded-xl px-4 py-3 flex items-center gap-3 ${
              saasMessage.type === 'success'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            <i
              className={`text-lg ${
                saasMessage.type === 'success'
                  ? 'ri-check-line text-emerald-500'
                  : 'ri-error-warning-line text-red-500'
              }`}
            />
            <p className="text-sm">{saasMessage.text}</p>
          </div>
        )}

        <div className="bg-background-100 rounded-2xl border border-background-200 p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                URL do Servidor Evolution API
              </label>
              <input
                type="text"
                value={saasConfig.evolution_server_url}
                onChange={(e) =>
                  setSaasConfig((prev) => ({ ...prev, evolution_server_url: e.target.value }))
                }
                placeholder="https://api.seuservidor.com.br"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all font-mono"
              />
              <p className="text-xs text-foreground-400 mt-1">
                URL pública do seu servidor Evolution API (onde todas as instâncias dos clientes rodarão)
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                API Key (Global Token)
              </label>
              <input
                type="password"
                value={saasConfig.evolution_api_key}
                onChange={(e) =>
                  setSaasConfig((prev) => ({ ...prev, evolution_api_key: e.target.value }))
                }
                placeholder="sua-chave-api-secreta"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all font-mono"
              />
              <p className="text-xs text-foreground-400 mt-1">
                Chave de autenticação do servidor (AUTHENTICATION_API_KEY do Docker)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-3 border-t border-background-200 flex-wrap">
            <button
              onClick={handleSaveSaas}
              disabled={saasSaving}
              className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {saasSaving ? (
                <>
                  <i className="ri-loader-4-line animate-spin" /> Salvando...
                </>
              ) : (
                <>
                  <i className="ri-save-line" /> Salvar configuração global
                </>
              )}
            </button>
            <button
              onClick={handleTestSaas}
              disabled={saasTesting || !saasConfig.evolution_server_url || !saasConfig.evolution_api_key}
              className="px-5 py-2 bg-secondary-500 hover:bg-secondary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {saasTesting ? (
                <>
                  <i className="ri-loader-4-line animate-spin" /> Testando...
                </>
              ) : (
                <>
                  <i className="ri-plug-line" /> Testar servidor
                </>
              )}
            </button>
          </div>
        </div>

        {/* Admin info card */}
        <div className="bg-accent-50 border border-accent-200 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-xl text-accent-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-accent-800 mb-1">Como funciona o modelo SaaS</p>
              <p className="text-xs text-accent-700 leading-relaxed">
                1. Configure acima o servidor Evolution API que você controla.
                <br />
                2. Quando um cliente acessar esta página, o WZap PRO cria uma instância automaticamente no seu servidor.
                <br />
                3. O cliente só precisa escanear o QR Code — não vê URL nem API Key.
              </p>
              <Link
                to="/tutorial/whatsapp"
                className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-accent-800 hover:text-accent-900 cursor-pointer"
              >
                Ver tutorial para clientes <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>

        {/* Admin can also see their own instance connection */}
        {isConfigured && (
          <div className="bg-background-100 rounded-2xl border border-background-200 p-6 space-y-5">
            <div>
              <h3 className="text-base font-heading font-semibold text-foreground-950">Sua instância (admin)</h3>
              <p className="text-xs text-foreground-500 mt-0.5">
                Esta é a instância vinculada à sua própria empresa
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-background-200 p-4">
                <p className="text-xs text-foreground-500 mb-1">Status</p>
                <div className="flex items-center gap-2">
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${
                      status === 'connected'
                        ? 'bg-emerald-500 animate-pulse'
                        : status === 'testing'
                          ? 'bg-amber-500'
                          : 'bg-red-500'
                    }`}
                  />
                  <span
                    className={`text-sm font-semibold ${
                      status === 'connected' ? 'text-emerald-600' : 'text-red-500'
                    }`}
                  >
                    {status === 'connected' ? 'Conectado' : status === 'testing' ? 'Testando...' : 'Desconectado'}
                  </span>
                </div>
                {keepAliveActive && (
                  <div className="mt-2 flex items-center gap-1.5 text-[10px] text-foreground-400">
                    <i className="ri-pulse-line text-emerald-500 text-[10px]" />
                    <span>Keep-alive ativo</span>
                    {lastCheck && <span>· Último check: {lastCheck}</span>}
                  </div>
                )}
                {status === 'disconnected' && instanceName && (
                  <button
                    onClick={handleReconnect}
                    disabled={reconnecting}
                    className="mt-2 w-full px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                  >
                    {reconnecting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" /> Gerando QR...
                      </>
                    ) : (
                      <>
                        <i className="ri-qr-code-line" /> Reconectar
                      </>
                    )}
                  </button>
                )}
              </div>
              <div className="bg-white rounded-xl border border-background-200 p-4">
                <p className="text-xs text-foreground-500 mb-1">Instância</p>
                <p className="text-sm font-semibold text-foreground-900">{instanceName || '—'}</p>
              </div>
              <div className="bg-white rounded-xl border border-background-200 p-4">
                <p className="text-xs text-foreground-500 mb-1">Endpoint</p>
                <p className="text-sm font-semibold text-foreground-900 truncate">{serverUrl || '—'}</p>
              </div>
            </div>

            {qrCode && (
              <div className="bg-white rounded-2xl border-2 border-dashed border-amber-300 p-6 text-center">
                <p className="text-sm font-semibold text-foreground-900 mb-1">
                  Escaneie o QR Code com o WhatsApp
                </p>
                <p className="text-xs text-foreground-500 mb-4">
                  Abra o WhatsApp no celular, vá em Aparelhos Conectados e escaneie
                </p>
                <img
                  src={`data:image/png;base64,${qrCode}`}
                  alt="QR Code WhatsApp"
                  className="w-52 h-52 mx-auto rounded-xl"
                />
                <button
                  onClick={() => setQrCode(null)}
                  className="mt-4 text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer underline"
                >
                  Fechar QR Code
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════
  // END USER VIEW
  // ═══════════════════════════════════════
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-bold text-foreground-950">WhatsApp</h2>
        <p className="text-sm text-foreground-500 mt-1">Conecte seu WhatsApp para enviar campanhas</p>
      </div>

      {/* Tutorial link */}
      <Link
        to="/tutorial/whatsapp"
        className="bg-accent-50 border border-accent-200 rounded-2xl p-5 flex items-start gap-4 hover:border-accent-300 transition-colors cursor-pointer group"
      >
        <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
          <i className="ri-youtube-fill text-xl text-accent-600" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-accent-800 mb-1 flex items-center gap-1">
            Primeira vez? Veja como conectar
            <i className="ri-arrow-right-line text-accent-500 group-hover:translate-x-0.5 transition-transform" />
          </p>
          <p className="text-xs text-accent-700 leading-relaxed">
            Tutorial rápido de 3 passos: abra o WhatsApp, escaneie o QR Code e pronto.
          </p>
        </div>
      </Link>

      {message && (
        <div
          className={`rounded-xl px-4 py-3 flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          <i
            className={`text-lg ${
              message.type === 'success'
                ? 'ri-check-line text-emerald-500'
                : 'ri-error-warning-line text-red-500'
            }`}
          />
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      {provisionMessage && (
        <div
          className={`rounded-xl px-4 py-3 flex items-center gap-3 ${
            provisionMessage.type === 'success'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          <i
            className={`text-lg ${
              provisionMessage.type === 'success'
                ? 'ri-check-line text-emerald-500'
                : 'ri-error-warning-line text-red-500'
            }`}
          />
          <p className="text-sm">{provisionMessage.text}</p>
        </div>
      )}

      {/* Not configured yet — show provision card */}
      {!isConfigured && (
        <div className="bg-white rounded-2xl border border-background-200 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-whatsapp-line text-3xl text-primary-500" />
          </div>
          <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
            Conecte seu WhatsApp
          </h3>
          <p className="text-sm text-foreground-500 max-w-sm mx-auto mb-6">
            Clique no botão abaixo para criar sua instância automaticamente. Depois é só escanear o QR Code.
          </p>
          <button
            onClick={handleProvision}
            disabled={provisioning}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
          >
            {provisioning ? (
              <>
                <i className="ri-loader-4-line animate-spin" /> Criando instância...
              </>
            ) : (
              <>
                <i className="ri-qr-code-line" /> Conectar WhatsApp
              </>
            )}
          </button>
          <p className="text-xs text-foreground-400 mt-3">
            Não precisa instalar nada. Levamos menos de 10 segundos.
          </p>
        </div>
      )}

      {/* Configured — show status and QR */}
      {isConfigured && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-background-100 rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 mb-1">Status</p>
              <div className="flex items-center gap-2">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    status === 'connected'
                      ? 'bg-emerald-500 animate-pulse'
                      : status === 'testing'
                        ? 'bg-amber-500'
                        : 'bg-red-500'
                  }`}
                />
                <span
                  className={`text-sm font-semibold ${
                    status === 'connected' ? 'text-emerald-600' : 'text-red-500'
                  }`}
                >
                  {status === 'connected'
                    ? 'Conectado'
                    : status === 'testing'
                      ? 'Testando...'
                      : 'Desconectado'}
                </span>
              </div>
              {keepAliveActive && (
                <div className="mt-2 flex items-center gap-1.5 text-[10px] text-foreground-400">
                  <i className="ri-pulse-line text-emerald-500 text-[10px]" />
                  <span>Keep-alive ativo</span>
                  {lastCheck && <span>· Último check: {lastCheck}</span>}
                </div>
              )}
              {status === 'disconnected' && instanceName && (
                <button
                  onClick={handleReconnect}
                  disabled={reconnecting}
                  className="mt-2 w-full px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                >
                  {reconnecting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> Gerando QR...
                    </>
                  ) : (
                    <>
                      <i className="ri-qr-code-line" /> Reconectar
                    </>
                  )}
                </button>
              )}
            </div>
            <div className="bg-background-100 rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 mb-1">Instância</p>
              <p className="text-sm font-semibold text-foreground-900">{instanceName || '—'}</p>
            </div>
            <div className="bg-background-100 rounded-xl border border-background-200 p-4">
              <p className="text-xs text-foreground-500 mb-1">Servidor</p>
              <p className="text-sm font-semibold text-foreground-900 truncate">{serverUrl || '—'}</p>
            </div>
          </div>

          {/* QR Code */}
          {qrCode && (
            <div className="bg-white rounded-2xl border-2 border-dashed border-amber-300 p-6 text-center">
              <p className="text-sm font-semibold text-foreground-900 mb-1">
                Escaneie o QR Code com o WhatsApp
              </p>
              <p className="text-xs text-foreground-500 mb-4">
                Abra o WhatsApp no celular, vá em Aparelhos Conectados e escaneie
              </p>
              <img
                src={`data:image/png;base64,${qrCode}`}
                alt="QR Code WhatsApp"
                className="w-52 h-52 mx-auto rounded-xl"
              />
              <button
                onClick={() => setQrCode(null)}
                className="mt-4 text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer underline"
              >
                Fechar QR Code
              </button>
            </div>
          )}

          {/* Test message */}
          <div className="bg-background-100 rounded-2xl border border-background-200 p-6 space-y-5">
            <div>
              <h3 className="text-base font-heading font-semibold text-foreground-950">
                Enviar Mensagem de Teste
              </h3>
              <p className="text-xs text-foreground-500 mt-0.5">
                Teste o envio real de mensagem para confirmar que está tudo funcionando
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                  Número do destinatário
                </label>
                <input
                  type="text"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="5511999999999"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all font-mono"
                />
                <p className="text-[10px] text-foreground-400 mt-1">
                  DDI + DDD + número, sem espaços
                </p>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleSendTest}
                  disabled={sendingTest || !testPhone.trim() || !instanceName}
                  className="w-full px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {sendingTest ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg" /> Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-lg" /> Enviar mensagem de teste
                    </>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Mensagem</label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all resize-none"
                placeholder="Digite a mensagem de teste..."
              />
              <p className="text-[10px] text-foreground-400 mt-1">{testMessage.length} caracteres</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}